import type { Shortcut } from "./types";

/**
 * Shortcuts handled by the WebReader itself, they can't be registered by the embedder.
 * A reserved shortcut also covers every combination adding modifiers to it
 * (e.g. `{ key: "arrowleft" }` reserves Shift+ArrowLeft and Mod+ArrowLeft).
 */
export const RESERVED_SHORTCUTS: readonly Readonly<Shortcut>[] = Object.freeze([
  // navigation & scrolling
  { key: "arrowleft" },
  { key: "arrowright" },
  { key: "arrowup" },
  { key: "arrowdown" },
  // keyboard accessibility (focus traps, dialogs, buttons activation)
  { key: "tab" },
  { key: "escape" },
  { key: "enter" },
  { key: " " },
]);

// single characters without case (digits, punctuation, ...) are already shifted
const isShiftedCharacter = (key: string) =>
  key.length === 1 && key.toLowerCase() === key.toUpperCase();

export function normalizeShortcut(shortcut: Shortcut): Required<Shortcut> {
  const key = shortcut.key.toLowerCase();
  return {
    key,
    mod: !!shortcut.mod,
    shift: !!shortcut.shift && !isShiftedCharacter(key),
  };
}

export function isSameShortcut(a: Shortcut, b: Shortcut): boolean {
  const [na, nb] = [normalizeShortcut(a), normalizeShortcut(b)];
  return na.key === nb.key && na.mod === nb.mod && na.shift === nb.shift;
}

export function isReservedShortcut(shortcut: Shortcut): boolean {
  const s = normalizeShortcut(shortcut);
  return RESERVED_SHORTCUTS.some(
    (r) => r.key === s.key && (!r.mod || s.mod) && (!r.shift || s.shift)
  );
}
